import {Box, Checkbox, FormControl, FormControlLabel, Menu, Radio,} from '@mui/material';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import {isCheckboxFilter} from '../../../utils/filtersHelper';
import {AndFilter, Filter, FilterCombinator, FilterGroup, NotFilter,} from '../../../utils/filter';
import {useEffect, useState} from 'react';

const useStyles = makeStyles((theme) =>
    createStyles({
        paper: {
            border: '1px solid ' + theme.palette.divider,
            '& .MuiMenu-paper': {
                marginTop: 2
            }
        },
        group: {
            borderTop: '1px solid ' + theme.palette.divider,
        },
        colName: {
            minWidth: '150px',
        },
        formControl: {
            margin: theme.spacing(3),
        },
        listItem: {
            '&:hover, body>&': {
                backgroundColor: theme.palette.action.hover,
            },
            transition: theme.transitions.create('background-color', {
                duration: theme.transitions.duration.shortest,
            }),
            zIndex: theme.zIndex.modal,
        },
    })
);

type FilterProps = {
    onChangeSelectedFilter: (filter: { parameters: Filter }) => void; //useCallback
    onChangeSelectedFilterGroups?: (filterGroup: FilterGroup[]) => void;
    defaultFilters: FilterGroup[];
    anchorEl?: Element;
    open: boolean;
    onClose(): void;
};

export function getSelectedFilterExpression(groups: FilterGroup[]): Filter {
    let selectedFilters: Filter[] = [];
    groups.forEach((group) => {
        const selected = group.set.filter((i) => i.selected).map((i) => i.filter);
        if (selected.length > 0) {
            selectedFilters.push(
                group.multiple
                    ? FilterCombinator.combine(selected, group.operator)
                    : selected[0]
            );
        }
        if (selected.length === 0 && group.multiple) {
            selectedFilters.push(
                NotFilter.create(FilterCombinator.combine(group.set.map(f => f.filter), group.operator))
            );
        }
    });
    return AndFilter.combine(selectedFilters);
}

export default function AdminDataGridFilter({
                                                defaultFilters,
                                                onChangeSelectedFilter,
                                                onChangeSelectedFilterGroups,
                                                ...props
                                            }: FilterProps) {
    const classes = useStyles();
    const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(
        defaultFilters ?? []
    );

    useEffect(() => {
        if (defaultFilters && defaultFilters.length) {
            const groups = defaultFilters.map(group => {
                if (group.multiple) return {...group, selected: !group.set.some(i => !i.selected)};
                else return {...group, selected: !group.set.some(i => i.selected)};
            });
            setFilterGroups(groups);
            onChangeSelectedFilter({
                parameters: getSelectedFilterExpression(defaultFilters),
            });
        }
    }, [defaultFilters, onChangeSelectedFilter]);




    function handleChangeSelected(
        checked: boolean,
        groupIndex: number,
        filterIndex: number
    ) {
        let group = filterGroups[groupIndex];
        const filter = {...group.set[filterIndex], selected: checked};
        if (group.multiple) {
            group.set[filterIndex] = filter;
        } else {
            group.set = group.set.map((f) => ({...f, selected: false}));
            group.set[filterIndex] = filter;
            group.selected = false;
        }
        let newGroups = [...filterGroups];
        newGroups[groupIndex] = group;
        setFilterGroups(newGroups);
        onChangeSelectedFilterGroups?.(newGroups);
        onChangeSelectedFilter({
            parameters: getSelectedFilterExpression(newGroups),
        });
    }

    function handleGeneralChangeSelected(
        checked: boolean,
        groupIndex: number,
    ) {
        let group = filterGroups[groupIndex];
        if (group.multiple) {
            group.set = group.set.map(f => ({...f, selected: checked}));
        } else {
            if (checked) {
                group.set = group.set.map((f) => ({...f, selected: false}));
            } else {
                group.set = group.set.map((f) => ({...f, selected: false}));
                group.set[0].selected = true;
            }
        }
        let newGroups = [...filterGroups];
        newGroups[groupIndex] = {...group, selected: checked};
        setFilterGroups(newGroups);
        onChangeSelectedFilter({
            parameters: getSelectedFilterExpression(newGroups),
        });
    }

    return (
        <Menu
            className={classes.paper}
            elevation={1}
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            transformOrigin={{vertical: 'top', horizontal: 'center'}}
            id="columns-context-menu"
            anchorEl={props.anchorEl}
            open={props.open}
            onClose={props.onClose}
        >
            <FormControl component="fieldset" className={classes.formControl}>
                {filterGroups.filter(g => !g.hidden).map((group, groupIndex) => {
                    return (
                        <Box className={classes.group} key={groupIndex}>
                            {group.set.map((filter, filterIndex) => (
                                <Box key={`${groupIndex}_${filterIndex}`}>
                                    <FormControlLabel
                                        control={
                                            isCheckboxFilter(filter) ? (
                                                <Checkbox
                                                    checkedIcon={<CheckBoxOutlinedIcon/>}
                                                    checked={filter.selected ?? false}
                                                    onChange={(evt, checked) =>
                                                        handleChangeSelected(
                                                            checked,
                                                            groupIndex,
                                                            filterIndex
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <Radio
                                                    checked={filter.selected ?? false}
                                                    onChange={(e, checked) =>
                                                        handleChangeSelected(
                                                            checked,
                                                            groupIndex,
                                                            filterIndex
                                                        )
                                                    }
                                                />
                                            )
                                        }
                                        label={filter.label}
                                    />
                                </Box>
                            ))}
                            {
                                group.set.length > 0 && group.includeAlle &&
                                <Box key={`${groupIndex}_alle`}>
                                    <FormControlLabel
                                        control={
                                            isCheckboxFilter(group.set[0]) ? (
                                                <Checkbox
                                                    checkedIcon={<CheckBoxOutlinedIcon/>}
                                                    checked={group.selected ?? false}
                                                    onChange={(evt, checked) =>
                                                        handleGeneralChangeSelected(
                                                            checked,
                                                            groupIndex,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <Radio
                                                    checked={group.selected ?? false}
                                                    onChange={(e, checked) =>
                                                        handleGeneralChangeSelected(
                                                            checked,
                                                            groupIndex,
                                                        )
                                                    }
                                                />
                                            )
                                        }
                                        label={'Vis alle'}
                                    />
                                </Box>
                            }

                        </Box>
                    );
                })}
            </FormControl>
        </Menu>
    );
}
